import { FormEvent, useEffect, useState } from 'react';

import { useApproveOrderKRWMutation } from '@entities/orders';
import { useLazyOrderCustomerProfilerQuery } from '@entities/orders/deposit';
import { FormItemWithError } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';
import { toPlainAmount } from '@shared/utils/formatAmount';

import { IOrderModalContextProps } from '../model/types';

interface IProps {
  entityId: string;
  requestedAmount: string;
  actualAmount: string | null;
  statusReason?: string | null;
  createdAt: string;
  entityType: 'order' | 'request';
  refetch: () => void;
}

export const ApproveOrderKRW = ({
  entityId,
  requestedAmount,
  actualAmount,
  statusReason,
  createdAt,
  entityType,
  refetch,
}: IProps) => {
  const [reason, setReason] = useState(statusReason || '');
  const [approveOrder, { isLoading, isError, error, isSuccess }] = useApproveOrderKRWMutation();
  const [fetchCustomerProfile, { data: customerProfileData, isLoading: isLoadingProfile }] =
    useLazyOrderCustomerProfilerQuery();

  const {
    closeModal,
    additionalContext: { setSelectedIds },
  } = useModal<IOrderModalContextProps>();

  // Fetch customer profile data when component mounts
  useEffect(() => {
    fetchCustomerProfile(entityId);
  }, [entityId, fetchCustomerProfile]);

  const customerProfile = customerProfileData?.data;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!customerProfile) {
      console.error('Customer profile data is not available');
      return;
    }

    try {
      const result = await approveOrder({
        id: entityId,
        actual_amount: actualAmount,
        reason,
        account_name: customerProfile.receiver_account_name,
        account_number: customerProfile.receiver_account_number,
        bank_name: customerProfile.receiver_bank_name,
      });

      if ('data' in result) {
        setSelectedIds([]);
        closeModal();
        refetch();
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoadingProfile) {
    return <div>Loading customer profile...</div>;
  }

  return (
    <>
      <h2>Approve {entityType === 'order' ? 'Order' : 'Request'} Korean WON</h2>

      <form className='popup-form' onSubmit={handleSubmit}>
        <FormItemWithError
          name='requested_amount'
          label='Requested Amount'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='requested_amount'
            id='requested_amount'
            value={`${toPlainAmount(requestedAmount)} KRW`}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError name='actual_amount' label='Actual Amount' isError={false} error={null}>
          <input
            type='text'
            name='actual_amount'
            id='actual_amount'
            value={`${toPlainAmount(actualAmount || '0')} KRW`}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          name='order_created_at'
          label='Order Created At'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='order_created_at'
            id='order_created_at'
            value={new Date(createdAt).toLocaleString()}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          name='sender_bank_name'
          label='Sender Bank Name'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='sender_bank_name'
            id='sender_bank_name'
            value={customerProfile?.sender_bank_name || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError name='sender_name' label='Sender Name' isError={false} error={null}>
          <input
            type='text'
            name='sender_name'
            id='sender_name'
            value={customerProfile?.sender_account_name || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          name='sender_account_number'
          label='Sender Account Number'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='sender_account_number'
            id='sender_account_number'
            value={customerProfile?.sender_account_number || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          name='receiver_bank_name'
          label='Receiver Bank Name'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='receiver_bank_name'
            id='receiver_bank_name'
            value={customerProfile?.receiver_bank_name || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError name='receiver_name' label='Receiver Name' isError={false} error={null}>
          <input
            type='text'
            name='receiver_name'
            id='receiver_name'
            value={customerProfile?.receiver_account_name || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          name='receiver_account_number'
          label='Receiver Account Number'
          isError={false}
          error={null}
        >
          <input
            type='text'
            name='receiver_account_number'
            id='receiver_account_number'
            value={customerProfile?.receiver_account_number || 'N/A'}
            readOnly
          />
        </FormItemWithError>

        <FormItemWithError
          secondName='order'
          name='reason'
          label='Reason'
          isError={isError}
          error={error}
        >
          <input
            type='text'
            name='reason'
            id='reason'
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder='Enter reason for approval'
          />
        </FormItemWithError>

        {isSuccess && <div className='form-success'>Successfully approved</div>}

        <div className='form-submit'>
          <button type='submit' disabled={isLoading || !customerProfile}>
            {isLoading ? 'Approving...' : 'Approve'}
          </button>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};
